import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { Eye, ShieldCheck, Zap, Target, Rocket, ArrowRight, Users, TrendingUp, Award, Clock, Globe } from 'lucide-react'
import Navbar from '../components/Common/Navbar'
import Footer from '../components/Common/Footer'

const B = 'https://pixner.net/bankio/main/assets/images'

const FadeUp = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  )
}

const values = [
  { icon: <Eye size={22} />, title: 'Transparency', desc: 'Clear pricing, no hidden fees, and honest communication — always.' },
  { icon: <Zap size={22} />, title: 'Reliability', desc: '99.9% uptime SLA. Our infrastructure never sleeps so your business never stops.' },
  { icon: <ShieldCheck size={22} />, title: 'Security', desc: 'Bank-grade security. Your customers\' data is protected at every step.' },
  { icon: <Zap size={22} />, title: 'Innovation', desc: 'Constantly improving our technology to stay ahead of the curve for you.' },
]

const team = [
  { name: 'Rajan Sharma', role: 'CEO & Founder', img: `${B}/testomonial.png` },
  { name: 'Priya Singh', role: 'CTO', img: `${B}/testomonial.png` },
  { name: 'Amit Verma', role: 'Head of Payments', img: `${B}/testomonial.png` },
  { name: 'Sneha Gupta', role: 'Head of Growth', img: `${B}/testomonial.png` },
]

export default function AboutUs() {
  return (
    <>
      <style>{css}</style>
      <div className="ab-root">
        <Navbar />

        {/* Hero */}
        <section className="ab-hero">
          <div className="ab-container ab-hero-grid">
            <motion.div className="ab-hero-text"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}>
              <span className="ab-sub">About A1XPAY</span>
              <h1 className="ab-hero-h1">Built for Indian Businesses,<br />Powered by Technology</h1>
              <p className="ab-hero-p">
                A1XPAY is a full-stack payment and technology company helping businesses of all sizes accept payments, build software, and grow online.
              </p>
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Link to="/register" className="ab-btn-primary">Get Started <ArrowRight size={16} /></Link>
              </motion.div>
            </motion.div>
            <motion.div className="ab-hero-visual"
              initial={{ opacity: 0, x: 40, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>
              <div className="ab-hv-card">
                <div className="ab-hv-head">Company at a glance</div>
                <div className="ab-hv-stats-mini">
                  {[
                    { icon: <Users size={16} />, val: '200+', label: 'Businesses' },
                    { icon: <TrendingUp size={16} />, val: '₹10Cr+', label: 'Monthly Volume' },
                    { icon: <Award size={16} />, val: '99.9%', label: 'Uptime' },
                    { icon: <Clock size={16} />, val: '24h', label: 'Activation' },
                  ].map((s, i) => (
                    <motion.div key={s.label} className="ab-hv-sstat"
                      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + i * 0.1 }}>
                      <div className="ab-hv-sstat-icon">{s.icon}</div>
                      <div className="ab-hv-sstat-val">{s.val}</div>
                      <div className="ab-hv-sstat-lbl">{s.label}</div>
                    </motion.div>
                  ))}
                </div>
                <div className="ab-hv-divider" />
                <div className="ab-hv-tags">
                  {['RBI Registered', 'NPCI Partner', 'Secure Payments'].map(t => (
                    <span key={t} className="ab-hv-tag">{t}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Mission */}
        <section className="ab-section ab-bg-white">
          <div className="ab-container ab-feat-grid">
            <FadeUp className="ab-feat-text">
              <div className="ab-head-circle"><Target size={26} color="#fff" /></div>
              <span className="ab-sub ab-green">Our Mission</span>
              <h2 className="ab-section-h2">Making payments simple for every Indian business</h2>
              <p className="ab-para">
                We started A1XPAY with one goal: remove the friction from accepting payments and building technology for businesses in India. Too many great businesses were losing customers because of a broken checkout experience or an unreliable payment partner.
              </p>
              <p className="ab-para">
                Today we serve 200+ businesses across India, processing thousands of transactions daily — from neighborhood shops to enterprise clients.
              </p>
              <Link to="/register" className="ab-btn-primary">Start for free</Link>
            </FadeUp>
            <FadeUp delay={0.1} className="ab-feat-img">
              <img src={`${B}/feature-item-1.png`} alt="Our mission" />
            </FadeUp>
          </div>
        </section>

        {/* Stats */}
        <section className="ab-section ab-bg-light">
          <div className="ab-container">
            <FadeUp>
              <div className="ab-stats-grid">
                {[
                  { num: '200+', label: 'Businesses served' },
                  { num: '99.9%', label: 'Uptime SLA' },
                  { num: '₹10Cr+', label: 'Processed monthly' },
                  { num: '50+', label: 'Banks supported' },
                ].map((s) => (
                  <div key={s.label} className="ab-stat-card">
                    <div className="ab-stat-num">{s.num}</div>
                    <div className="ab-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </section>

        {/* Values */}
        <section className="ab-section ab-bg-white">
          <div className="ab-container">
            <FadeUp>
              <div className="ab-section-center">
                <span className="ab-sub ab-green">What drives us</span>
                <h2 className="ab-section-h2">Our Core Values</h2>
                <p className="ab-para ab-para-c">
                  These values guide every product decision, every support interaction, and every line of code we write.
                </p>
              </div>
            </FadeUp>
            <div className="ab-values-grid">
              {values.map((v, i) => (
                <FadeUp key={v.title} delay={i * 0.1}>
                  <div className="ab-value-card">
                    <div className="ab-value-icon-wrap">{v.icon}</div>
                    <h4 className="ab-value-title">{v.title}</h4>
                    <p className="ab-value-desc">{v.desc}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="ab-section ab-bg-light">
          <div className="ab-container ab-feat-grid ab-feat-reverse">
            <FadeUp delay={0.1} className="ab-feat-img">
              <img src={`${B}/feature-item-2.png`} alt="Our story" />
            </FadeUp>
            <FadeUp className="ab-feat-text">
              <div className="ab-head-circle"><Rocket size={24} color="#fff" /></div>
              <span className="ab-sub ab-green">Our Story</span>
              <h2 className="ab-section-h2">From a small idea to a growing platform</h2>
              <p className="ab-para">
                A1XPAY was founded with the conviction that technology should work for businesses, not against them. We saw too many small and medium businesses struggling with unreliable payment systems and overpriced tech vendors.
              </p>
              <p className="ab-para">
                We built A1XPAY to be the payment and technology partner we always wished existed — transparent pricing, world-class reliability, and a team that actually picks up the phone.
              </p>
            </FadeUp>
          </div>
        </section>

        {/* Team */}
        <section className="ab-section ab-bg-white">
          <div className="ab-container">
            <FadeUp>
              <div className="ab-section-center">
                <span className="ab-sub ab-green">The people behind the product</span>
                <h2 className="ab-section-h2">Meet the Team</h2>
              </div>
            </FadeUp>
            <div className="ab-team-grid">
              {team.map((m, i) => (
                <FadeUp key={m.name} delay={i * 0.1}>
                  <div className="ab-team-card">
                    <img src={m.img} alt={m.name} className="ab-team-avatar"
                      onError={e => { e.target.style.background = '#EEF3FF' }} />
                    <div className="ab-team-name">{m.name}</div>
                    <div className="ab-team-role">{m.role}</div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* World reach section */}
        <section className="ab-section ab-bg-light">
          <div className="ab-container ab-feat-grid">
            <FadeUp className="ab-feat-text">
              <div className="ab-head-circle"><Globe size={26} color="#fff" /></div>
              <span className="ab-sub ab-green">Our reach</span>
              <h2 className="ab-section-h2">Built for India, ready for the world</h2>
              <p className="ab-para">A1XPAY's payment infrastructure is built on India's most reliable banking rails, with support for international cards and cross-border transactions on the Enterprise plan.</p>
              <div className="ab-reach-stats">
                {[['15+', 'States served'], ['60+', 'Bank integrations'], ['100+', 'Payment methods'], ['3 min', 'Avg onboarding']].map(([v, l]) => (
                  <div key={l} className="ab-rstat">
                    <div className="ab-rstat-val">{v}</div>
                    <div className="ab-rstat-lbl">{l}</div>
                  </div>
                ))}
              </div>
            </FadeUp>
            <FadeUp delay={0.1} className="ab-feat-img">
              <motion.img src={`${B}/world-map.png`} alt="Global reach"
                whileHover={{ scale: 1.03 }} transition={{ type: 'spring', stiffness: 180 }} />
            </FadeUp>
          </div>
        </section>

        {/* Office / team illustration */}
        <section className="ab-section ab-bg-white">
          <div className="ab-container ab-feat-grid ab-feat-rev">
            <FadeUp delay={0.1} className="ab-feat-img">
              <motion.img src={`${B}/about-img-1.png`} alt="About A1XPAY"
                whileHover={{ scale: 1.03 }} transition={{ type: 'spring', stiffness: 180 }} />
            </FadeUp>
            <FadeUp className="ab-feat-text">
              <div className="ab-head-circle"><Users size={26} color="#fff" /></div>
              <span className="ab-sub ab-green">Our culture</span>
              <h2 className="ab-section-h2">A team obsessed with your success</h2>
              <p className="ab-para">Every person at A1XPAY is here because they believe in our mission: making digital payments work for every business, big or small. We hire for attitude, train for skill, and retain through culture.</p>
              <ul className="ab-list">
                {['Remote-first team across India', 'Direct access to founders for enterprise clients', 'Dedicated integration engineers for complex needs', 'On-call support during peak business hours'].map(t => (
                  <li key={t} className="ab-li"><div className="ab-dot" /><span>{t}</span></li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </section>

        {/* CTA */}
        <section className="ab-cta">
          <div className="ab-container ab-cta-flex">
            <FadeUp className="ab-cta-left">
              <span className="ab-sub ab-white">Ready to get started?</span>
              <h2 className="ab-cta-h2">Join 200+ businesses on A1XPAY</h2>
              <div className="ab-cta-btns">
                <Link to="/register" className="ab-btn-white">Open Account</Link>
                <Link to="/contact-us" className="ab-btn-ghost-w">Contact us</Link>
              </div>
            </FadeUp>
            <motion.div className="ab-cta-illu"
              initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}>
              <img src={`${B}/get-start.png`} alt="Get started" />
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;800;900&display=swap');
.ab-root { font-family: 'Jost', sans-serif; background: #fff; overflow-x: hidden; }
.ab-container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
@media (min-width:1200px) { .ab-container { padding: 0 2.5rem; } }
.ab-hero { background: #f5faff; padding: 90px 0 70px; position: relative; overflow: hidden; }
.ab-hero::before { content: ''; position: absolute; width: 500px; height: 500px; border-radius: 50%; background: rgba(26,77,190,0.07); top: -150px; right: -100px; pointer-events: none; }
.ab-hero-grid { display: grid; grid-template-columns: 1fr; gap: 3rem; align-items: center; position: relative; z-index: 2; }
@media (min-width:1024px) { .ab-hero-grid { grid-template-columns: 1fr 1fr; } }
.ab-hero-text { max-width: 560px; }
.ab-hero-visual { display: none; }
@media (min-width:1024px) { .ab-hero-visual { display: block; } }
.ab-hv-card { background: #fff; border: 1.5px solid #E8EBF4; border-radius: 1.25rem; padding: 1.75rem; box-shadow: 0 12px 40px rgba(26,77,190,0.12); }
.ab-hv-head { font-size: 0.8125rem; font-weight: 700; color: #64748B; margin-bottom: 1.25rem; text-transform: uppercase; letter-spacing: 0.06em; }
.ab-hv-stats-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 0.875rem; margin-bottom: 1.25rem; }
.ab-hv-sstat { background: #f5faff; border-radius: 0.75rem; padding: 0.875rem; text-align: center; }
.ab-hv-sstat-icon { display: flex; justify-content: center; color: #1A4DBE; margin-bottom: 0.375rem; }
.ab-hv-sstat-val { font-size: 1.25rem; font-weight: 800; color: #0A1023; letter-spacing: -0.03em; }
.ab-hv-sstat-lbl { font-size: 0.7rem; color: #64748B; font-weight: 500; margin-top: 0.15rem; }
.ab-hv-divider { height: 1px; background: #E8EBF4; margin-bottom: 1rem; }
.ab-hv-tags { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.ab-hv-tag { font-size: 0.75rem; font-weight: 600; color: #1A4DBE; background: #EEF3FF; border-radius: 50px; padding: 0.25rem 0.75rem; }
.ab-sub { font-size: 1rem; font-weight: 600; color: #055C2D; margin-bottom: 0.875rem; display: block; }
.ab-green { color: #055C2D; }
.ab-white { color: rgba(255,255,255,0.85); }
.ab-hero-h1 { font-size: clamp(2rem, 4.5vw, 3.5rem); font-weight: 800; color: #0A1023; letter-spacing: -0.04em; line-height: 1.1; margin-bottom: 1.25rem; }
.ab-hero-p { font-size: 1.0625rem; color: #2C3655; line-height: 1.75; max-width: 480px; margin-bottom: 1.75rem; }
.ab-section { padding: 80px 0; }
.ab-bg-white { background: #fff; }
.ab-bg-light { background: #f5faff; }
.ab-feat-grid { display: grid; grid-template-columns: 1fr; gap: 3rem; align-items: center; }
@media (min-width:1024px) { .ab-feat-grid { grid-template-columns: 1fr 1fr; gap: 5rem; } }
@media (min-width:1024px) { .ab-feat-reverse .ab-feat-img { order: -1; } }
.ab-feat-img { display: flex; align-items: center; justify-content: center; }
.ab-feat-img img { max-width: 100%; max-height: 420px; object-fit: contain; }
.ab-head-circle { width: 58px; height: 58px; border-radius: 50%; background: linear-gradient(135deg, #1A4DBE 0%, #06B6D4 100%); margin-bottom: 1.5rem; display: flex; align-items: center; justify-content: center; box-shadow: 0 6px 20px rgba(26,77,190,0.28); }
.ab-section-h2 { font-size: clamp(1.75rem, 3.5vw, 2.625rem); font-weight: 700; color: #0A1023; letter-spacing: -0.03em; line-height: 1.2; margin-bottom: 1rem; }
.ab-para { font-size: 1rem; color: #2C3655; line-height: 1.75; margin-bottom: 1.25rem; max-width: 520px; }
.ab-para-c { text-align: center; margin-left: auto; margin-right: auto; max-width: 580px; }
.ab-section-center { text-align: center; margin-bottom: 3rem; }
.ab-btn-primary { display: inline-flex; align-items: center; padding: 0.7rem 1.75rem; font-size: 1rem; font-weight: 600; color: #fff; background: #1A4DBE; border: 1.5px solid #1A4DBE; border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; }
.ab-btn-primary:hover { background: #1540a8; transform: translateY(-1px); }
.ab-btn-white { display: inline-flex; align-items: center; padding: 0.7rem 1.75rem; font-size: 1rem; font-weight: 600; color: #1A4DBE; background: #fff; border: 1.5px solid #fff; border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; }
.ab-btn-white:hover { background: #f0f4ff; }
.ab-btn-ghost-w { display: inline-flex; align-items: center; padding: 0.7rem 1.75rem; font-size: 1rem; font-weight: 600; color: #fff; background: transparent; border: 1.5px solid rgba(255,255,255,0.55); border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; }
.ab-btn-ghost-w:hover { border-color: #fff; }
.ab-stats-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem; }
@media (min-width:768px) { .ab-stats-grid { grid-template-columns: repeat(4, 1fr); } }
.ab-stat-card { background: #fff; border-radius: 1rem; padding: 2rem 1.5rem; text-align: center; box-shadow: 0 4px 20px rgba(47,65,129,0.08); border: 1.5px solid #E8EBF4; transition: transform 0.2s, box-shadow 0.2s; cursor: default; }
.ab-stat-card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(26,77,190,0.15); }
.ab-stat-num { font-size: 2.5rem; font-weight: 800; color: #1A4DBE; letter-spacing: -0.04em; margin-bottom: 0.4rem; }
.ab-stat-label { font-size: 0.9375rem; color: #2C3655; font-weight: 500; }
.ab-values-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem; }
@media (min-width:768px) { .ab-values-grid { grid-template-columns: repeat(4, 1fr); } }
.ab-value-card { background: #f5faff; border-radius: 1rem; padding: 1.75rem 1.25rem; text-align: center; border: 1.5px solid #E8EBF4; transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s; cursor: default; }
.ab-value-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(26,77,190,0.12); border-color: #B0BAF1; }
.ab-value-icon-wrap { width: 3.5rem; height: 3.5rem; background: #EEF3FF; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem; color: #1A4DBE; }
.ab-value-title { font-size: 1.0625rem; font-weight: 700; color: #0A1023; margin-bottom: 0.5rem; }
.ab-value-desc { font-size: 0.875rem; color: #2C3655; line-height: 1.65; }
.ab-team-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem; }
@media (min-width:768px) { .ab-team-grid { grid-template-columns: repeat(4, 1fr); } }
.ab-team-card { background: #f5faff; border-radius: 1rem; padding: 2rem 1.25rem; text-align: center; }
.ab-team-avatar { width: 72px; height: 72px; border-radius: 50%; object-fit: cover; margin: 0 auto 1rem; display: block; background: #EEF3FF; }
.ab-team-name { font-size: 1rem; font-weight: 700; color: #0A1023; margin-bottom: 0.25rem; }
.ab-team-role { font-size: 0.875rem; color: #64748B; }
/* Reversed feat grid */
.ab-feat-rev .ab-feat-img { order: 0; }
@media (min-width:1024px) { .ab-feat-rev .ab-feat-img { order: -1; } }
/* List inside feat sections */
.ab-list { list-style: none; padding: 0; margin: 0 0 1.75rem; display: flex; flex-direction: column; gap: 0.75rem; }
.ab-li { display: flex; align-items: center; gap: 0.625rem; font-size: 1rem; color: #2C3655; font-weight: 500; }
.ab-dot { min-width: 22px; min-height: 22px; border-radius: 50%; background: #055C2D; display: flex; align-items: center; justify-content: center; }
.ab-dot::after { content: ''; width: 8px; height: 5px; border-left: 2px solid #fff; border-bottom: 2px solid #fff; transform: rotate(-45deg); display: block; margin-top: -2px; }
/* Reach stats row */
.ab-reach-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.875rem; margin-top: 1.5rem; }
.ab-rstat { background: #fff; border: 1.5px solid #E8EBF4; border-radius: 0.875rem; padding: 1rem; text-align: center; }
.ab-rstat-val { font-size: 1.5rem; font-weight: 800; color: #1A4DBE; letter-spacing: -0.03em; }
.ab-rstat-lbl { font-size: 0.8rem; color: #64748B; font-weight: 500; margin-top: 0.2rem; }
/* CTA */
.ab-cta { background: #1A4DBE; padding: 80px 0; position: relative; overflow: hidden; }
.ab-cta::before { content: ''; position: absolute; width: 400px; height: 400px; border-radius: 50%; background: rgba(255,255,255,0.05); top: -150px; right: -80px; }
.ab-cta-flex { position: relative; z-index: 2; display: flex; align-items: center; justify-content: space-between; gap: 2rem; flex-wrap: wrap; }
.ab-cta-left { flex: 1; min-width: 280px; }
.ab-cta-h2 { font-size: clamp(1.875rem, 4vw, 2.75rem); font-weight: 800; color: #fff; letter-spacing: -0.04em; margin-bottom: 2rem; }
.ab-cta-btns { display: flex; gap: 1rem; flex-wrap: wrap; }
.ab-cta-illu { display: none; }
@media (min-width:900px) { .ab-cta-illu { display: block; width: 200px; flex-shrink: 0; } }
.ab-cta-illu img { max-width: 100%; filter: brightness(1.1) drop-shadow(0 8px 24px rgba(0,0,0,0.2)); }
`
